import React from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { ArrowUpRight, Calendar, Building2 } from "lucide-react";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Reveal, Stagger, Item } from "@/components/Reveal";
import { useFetch } from "@/lib/useData";
import RichHtml, { isHtmlEmpty } from "@/components/RichHtml";

export function Projects() {
  const { data: projects } = useFetch("/projects");
  return (
    <>
      <SEO title="Projects | Infinity Medical Group" description="Selected projects and initiatives from across the IMG network." />
      <Breadcrumbs />
      <section className="bg-white pt-4 pb-14">
        <div className="container">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-teal">
            <span className="h-px w-6 bg-teal" /> Projects & initiatives
          </span>
          <h1 className="mt-5 font-display font-bold text-4xl md:text-6xl text-navy-800 tracking-tight max-w-3xl">Selected projects across the IMG network.</h1>
          <p className="mt-6 max-w-2xl text-lg text-navy-700/75">Detailed case studies will be published as projects are documented. Use the contact form to discuss recent work in more detail.</p>
        </div>
      </section>
      <section className="bg-soft-gray py-16 lg:py-24">
        <div className="container">
          <Stagger className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {(projects || []).map((p) => (
              <Item key={p.slug}>
                <Link to={`/projects/${p.slug}`} data-testid={`project-card-${p.slug}`} className="block h-full p-7 rounded-2xl bg-white border border-navy-50 hover:shadow-card transition-shadow">
                  <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-teal">
                    <Building2 className="h-3.5 w-3.5" /> {p.sector}
                  </span>
                  <h3 className="mt-4 font-display font-semibold text-xl text-navy-800">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-navy-700/75">{p.summary}</p>
                  <div className="mt-6 flex items-center justify-between">
                    <span className="text-xs text-navy-500 inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {p.year || "Details coming soon"}</span>
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal">Details <ArrowUpRight className="h-4 w-4" /></span>
                  </div>
                </Link>
              </Item>
            ))}
          </Stagger>
        </div>
      </section>
    </>
  );
}

export function ProjectDetail() {
  const { slug } = useParams();
  const { pathname } = useLocation();
  const { data } = useFetch(`/projects/${slug}`, [slug]);
  if (!data) return <div className="min-h-screen pt-32" />;
  return (
    <>
      <SEO title={`${data.title} | IMG Projects`} description={data.summary} path={pathname} />
      <Breadcrumbs overrideLast={data.title} />
      <section className="bg-white pt-4 pb-16">
        <div className="container max-w-3xl">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-teal">
            <Building2 className="h-3.5 w-3.5" /> {data.sector}
          </span>
          <h1 className="mt-5 font-display font-bold text-4xl md:text-6xl text-navy-800 tracking-tight">{data.title}</h1>
          <p className="mt-6 text-lg leading-relaxed text-navy-700/80">{data.summary}</p>
          {!isHtmlEmpty(data.body_html) ? (
            <div className="mt-8" data-testid="project-body">
              <RichHtml html={data.body_html} />
            </div>
          ) : (
            <div className="mt-10 p-8 rounded-2xl bg-soft-gray border border-navy-50">
              <p className="text-navy-700/80">Details will be published soon. Contact us to learn more about our recent work.</p>
              <Link to="/contact?subject=Media+enquiry" className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-navy text-white text-sm font-semibold hover:bg-teal transition-colors">
                Contact us <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
